import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Product, ProductAsset } from "@/types/models/product";
import { NavigationHeader } from "@/components/common/NavigationHeader";
import { CloudDownload, Save, AlertTriangle } from "lucide-react";
import {
  StandardFormInput,
  StandardFormSelect,
  StandardFormTagInput,
  StandardFormMultiSelect,
} from "@/components/common/StandardFormInput";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import ImageUpload from "@/components/ImageUpload/ImageUpload";
import {
  productVariant,
  VariantMappingData,
  ShippingDetails,
  ApiShippingDetails,
} from "@/types/models/product";
import { Category } from "@/types/models/category";
import { Tax } from "@/types/models/tax";
import QuillEditor from "@/components/product/QuillEditor";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/store";
import { fetchCategoriesRequest } from "@/store/slices/category.slice";
import { fetchTaxesRequest } from "@/store/slices/tax.slice";
import { uploadImageAction } from "@/store/actions/imageupload.actions";
import useProducts from "@/hooks/useProducts";
import { toast } from "sonner";
import VariantOptions, { VariantOption } from "@/components/product/VariantOptions";
import VariantTable, { VariantCombination } from "@/components/product/VariantTable";
import VariantDetailModal from "@/components/product/VariantDetailModal";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileManagerModal } from "@/components/fileManager/FileManagerModal";
import { FileManagerConfig, FileSystemItem } from "@/types/fileManager";
import { useGlobalLoading } from "@/hooks/useGlobalLoading";
import { ProductMetafieldsCard } from "@/components/metafields/ProductMetafieldsCard";
import { isStoreKycApprovedForPublishing } from "@/utils/storeKyc";

interface MetafieldValue {
  key: string;
  type: string;
  value: string | number | string[];
}

const NewProduct: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { storeType = "", storeId = "" } = params as {
    storeType?: string;
    storeId?: string;
  };
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("q");
  const isDuplicateMode = searchParams.get("duplicate") === "1";
  const sourceProductId = id || "";
  const isEditMode = Boolean(id && !isDuplicateMode);
  const dispatch = useAppDispatch();
  const catergoryList = useAppSelector((state: RootState) => state.category);
  const taxList = useAppSelector((state: RootState) => state.tax);
  const currentStoreInfo = useAppSelector((state: RootState) => state.auth.currentStoreInfo);

  const {
    currentItem: currentProduct,
    formLoading: loading,
    itemLoading,
    formSuccess: success,
    formError: error,
    createItem,
    updateItem,
    fetchItem,
    resetFormState,
  } = useProducts();

  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [showDraftConfirmDialog, setShowDraftConfirmDialog] =
    useState<boolean>(false);
  const [showSaveAsDraftDialog, setShowSaveAsDraftDialog] =
    useState<boolean>(false);
  const [publishValidationErrors, setPublishValidationErrors] = useState<string[]>([]);
  const [title, setTitle] = useState<string>("");
  const [continueSelling, setContinueSelling] = useState<boolean>(true);
  const [taxable, setTaxable] = useState<boolean>(false);
  const [category, setCategory] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState<string>("");
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [selectedTaxIds, setSelectedTaxIds] = useState<string[]>([]);
  const [metafields, setMetafields] = useState<MetafieldValue[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [description, setDescription] = useState<any>({ ops: [] });
  const [images, setImages] = useState<string[]>([]); // Asset IDs for backend
  const [assets, setAssets] = useState<Product["assets"]>([]); // Asset objects for preview
  const [videos, setVideos] = useState<string[]>([]);
  const [sizeCharts, setSizeCharts] = useState<string[]>([]);
  const [productVariants] = useState<Product["variants"][]>([]);
  const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);
  const [variantCombinations, setVariantCombinations] = useState<
    VariantCombination[]
  >([]);
  const [deletedVariants, setDeletedVariants] = useState<
    VariantCombination[]
  >([]);
  const [selectedVariant, setSelectedVariant] =
    useState<VariantCombination | null>(null);
  const [hasVariants, setHasVariants] = useState<boolean>(false);
  const { confirm } = useConfirmation();

  const [defaultVariant, setDefaultVariant] = useState<VariantCombination>(() => {
    const defaultId = `default-${Date.now()}`;
    return {
      id: defaultId,
      options: ["Default"],
      optionString: "Default",
      image: undefined,
      images: [],
      price: 0,
      listingPrice: 0,
      sku: "",
      barcode: "",
      trackInventory: false,
      inventory: 0,
      title: "Default Variant",
      description: "",
      shippingDetails: {
        weight: 0,
        weightUnit: "g",
        width: 0,
        height: 0,
        length: 0,
        shippingClass: "",
        freeShipping: false,
        shippingPrice: 0,
      },
      metafields: {},
      hsCode: undefined,
      mid: undefined,
    };
  });

  const [showImageManager, setShowImageManager] = useState(false);
  const [showVideoManager, setShowVideoManager] = useState(false);
  const [showSizeChartManager, setShowSizeChartManager] = useState(false);
  const [showVariantImageManager, setShowVariantImageManager] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");

  const HandleImageChange = async (file: File | null, id?: number) => {
    if (id !== undefined) {
      // Remove image at index
      setImages((prev: string[]) => {
        return prev.filter((_, i) => i !== id);
      });
      setAssets((prev) => {
        return prev?.filter((_, i) => i !== id) || [];
      });
      return;
    }
    if (!file) return;
    const assetId: string = await uploadImageAction(file, storeId);
    if (!assetId) return;
    
    // Add new image assetId
    setImages((prev: string[]) => [...prev, assetId]);
    
    // Build ProductAsset object for the uploaded image
    const newAsset: ProductAsset = {
      id: `temp-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      product_id: isEditMode ? id || "" : "",
      asset_id: assetId,
      position: images.length,
      usage: "image",
      asset: {
        id: assetId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        path: `public/store/${storeType}/${storeId}/products/images/${assetId}`,
        store_id: storeId,
        original_name: file.name,
        size: file.size,
        content_type: file.type,
        usage: "general",
        status: "active",
      },
    };
    
    setAssets((prev) => [...(prev || []), newAsset]);
  };

  const handleVideoChange = async (file: File | null, id?: number) => {
    if (id) {
      setVideos((prev: string[]) => {
        return prev.filter((_, i) => i !== id);
      });
      return;
    }
    if (!file) return;
    const video: string = await uploadImageAction(file, storeId);
    if (!video) return;
    setVideos((prev: string[]) => [...prev, video]);
  };

  const handleSizeChartChange = async (file: File | null, id?: number) => {
    if (id) {
      setSizeCharts((prev: string[]) => {
        return prev.filter((_, i) => i !== id);
      });
      return;
    }
    if (!file) return;
    const chart: string = await uploadImageAction(file, storeId);
    if (!chart) return;
    setSizeCharts((prev: string[]) => [...prev, chart]);
  };

  const handleDescriptionChange = (value: any) => {
    setDescription(value);
  };

  const handleImageSelect = (selectedFiles: FileSystemItem[]) => {
    const imageAssetIds = selectedFiles.map((file) => file.assetId);
    
    // Build ProductAsset objects for preview
    const newAssets: ProductAsset[] = selectedFiles.map((file, index) => {
      const position = (assets?.length || 0) + index;
      return {
        id: `temp-${Date.now()}-${index}`, // Temporary ID for new assets
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        product_id: isEditMode ? id || "" : "",
        asset_id: file.assetId,
        position: position,
        usage: "image",
        asset: {
          id: file.assetId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null,
          path: file.path,
          store_id: storeId,
          original_name: file.name,
          size: file.size || 0,
          content_type: file.contentType || file.mimeType || "image/png",
          usage: "general",
          status: "active",
        },
      };
    });
    
    setImages((prev) => [...prev, ...imageAssetIds]);
    setAssets((prev) => [...(prev || []), ...newAssets]);
    setShowImageManager(false);
  };

  const handleVideoSelect = (selectedFiles: FileSystemItem[]) => {
    const videoAssetIds = selectedFiles.map((file) => file.assetId);
    setVideos((prev) => [...prev, ...videoAssetIds]);
    setShowVideoManager(false);
  };

  const handleSizeChartSelect = (selectedFiles: FileSystemItem[]) => {
    const chartAssetIds = selectedFiles.map((file) => file.assetId);
    setSizeCharts((prev) => [...prev, ...chartAssetIds]);
    setShowSizeChartManager(false);
  };

  const handleVariantImageSelect = (selectedFiles: FileSystemItem[]) => {
    if (selectedFiles.length === 0 || !selectedVariantId) return;
    const imageAssetIds = selectedFiles.map((file) => file.assetId);
    
    // Build asset objects for preview (similar to handleImageSelect)
    const newAssets = selectedFiles.map((file, index) => ({
      asset_id: file.assetId,
      asset: {
        id: file.assetId,
        path: file.path,
        original_name: file.name,
        size: file.size || 0,
        content_type: file.contentType || file.mimeType || "image/png",
      },
    }));
    
    if (hasVariants) {
      setVariantCombinations((prev) =>
        prev.map((variant) =>
          variant.id === selectedVariantId
            ? { ...variant, images: imageAssetIds, image: imageAssetIds[0], variantAssets: newAssets }
            : variant,
        ),
      );
    } else {
      setDefaultVariant((prev) => ({ ...prev, images: imageAssetIds, image: imageAssetIds[0], variantAssets: newAssets }));
    }
    setShowVariantImageManager(false);
    setSelectedVariantId("");
  };

  const handleVariantImageUpload = (variantId: string) => {
    setSelectedVariantId(variantId);
    setShowVariantImageManager(true);
  };

  const handleVariantSave = (updatedVariant: VariantCombination) => {
    if (!hasVariants && updatedVariant.optionString === "Default") {
      setDefaultVariant(updatedVariant);
    } else {
      setVariantCombinations((prev) =>
        prev.map((variant) =>
          variant.id === updatedVariant.id ? updatedVariant : variant,
        ),
      );
    }
  };

  const handleReenableVariant = (deletedVariant: VariantCombination) => {
    // Remove the variant from deleted list
    setDeletedVariants((prev) => prev.filter((v) => v.id !== deletedVariant.id));
    
    // Create a new variant without the ID (so server creates a new one)
    const reenabled: VariantCombination = {
      ...deletedVariant,
      id: `new-variant-${Date.now()}`, // New ID to indicate this is a new variant
    };
    
    // Add to active variants
    setVariantCombinations((prev) => [...prev, reenabled]);
    
    toast.success(`Variant "${deletedVariant.optionString}" has been re-enabled`);
  };

  const handleEnableVariant = (disabledVariant: VariantCombination) => {
    // Replace the disabled placeholder with an enabled one
    setVariantCombinations((prev) =>
      prev.map((variant) =>
        variant.id === disabledVariant.id
          ? {
              ...variant,
              id: `new-variant-${Date.now()}`, // New ID to indicate this needs to be created
              sku: `SKU-${prev.length}`,
              trackInventory: true,
            }
          : variant
      )
    );
    
    toast.success(`Variant "${disabledVariant.optionString}" has been enabled`);
  };

  const handleApplyToAll = (sourceVariant: VariantCombination) => {
    if (!hasVariants) {
      setDefaultVariant(sourceVariant);
    } else {
      setVariantCombinations((prev) =>
        prev.map((variant) => ({
          ...variant,
          price: sourceVariant.price,
          listingPrice: sourceVariant.listingPrice,
          trackInventory: sourceVariant.trackInventory,
          inventory: sourceVariant.inventory,
          shippingDetails: { ...sourceVariant.shippingDetails },
        })),
      );
    }
  };

  const hasDefaultVariantData = () => {
    const hasPriceData =
      (defaultVariant.price && defaultVariant.price > 0) ||
      (defaultVariant.listingPrice && defaultVariant.listingPrice > 0);
    const hasOtherData =
      (defaultVariant.sku && defaultVariant.sku.trim() !== "") ||
      (defaultVariant.barcode && defaultVariant.barcode.trim() !== "") ||
      defaultVariant.trackInventory ||
      (defaultVariant.inventory && defaultVariant.inventory > 0) ||
      (defaultVariant.title &&
        defaultVariant.title.trim() !== "" &&
        defaultVariant.title !== "Default Variant") ||
      (defaultVariant.description && defaultVariant.description.trim() !== "") ||
      (defaultVariant.shippingDetails &&
        (defaultVariant.shippingDetails.weight > 0 ||
          defaultVariant.shippingDetails.width > 0 ||
          defaultVariant.shippingDetails.height > 0 ||
          defaultVariant.shippingDetails.length > 0));

    return hasPriceData || hasOtherData;
  };

  const handleVariantToggle = async (value: boolean) => {
    if (value === hasVariants) return;
    if (value && hasDefaultVariantData()) {
      const result = await confirm({
        title: "Enable Product Variants?",
        message:
          "The default variant contains data that will be lost if you enable variants. This includes pricing, inventory, SKU, and other variant-specific information. Are you sure you want to continue?",
        confirmText: "Yes, Enable Variants",
        cancelText: "Cancel",
        type: "destructive",
      });

      if (result.confirmed) {
        performVariantToggle(value);
      }
    } else {
      performVariantToggle(value);
    }
  };

  const performVariantToggle = (value: boolean) => {
    setHasVariants(value);
    if (value) {
      setVariantOptions([]);
      setVariantCombinations([]);
    } else {
      setVariantOptions([]);
      setVariantCombinations([]);
      setDefaultVariant((prev) => ({
        ...prev,
        id: prev.id,
        options: ["Default"],
        optionString: "Default Variant",
      }));
    }
  };

  const imageManagerConfig: FileManagerConfig = {
    allowedFileTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ],
    maxFileSize: 10 * 1024 * 1024,
    maxSelections: 10,
    selectionMode: "multiple",
    showUpload: true,
    showCreateFolder: true,
    onSelect: handleImageSelect,
    onCancel: () => setShowImageManager(false),
  };

  const videoManagerConfig: FileManagerConfig = {
    allowedFileTypes: [
      "video/mp4",
      "video/mov",
      "video/avi",
      "video/webm",
      "video/mkv",
    ],
    maxFileSize: 100 * 1024 * 1024,
    maxSelections: 5,
    selectionMode: "multiple",
    showUpload: true,
    showCreateFolder: true,
    onSelect: handleVideoSelect,
    onCancel: () => setShowVideoManager(false),
  };

  const sizeChartManagerConfig: FileManagerConfig = {
    allowedFileTypes: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
    maxFileSize: 5 * 1024 * 1024,
    maxSelections: 3,
    selectionMode: "multiple",
    showUpload: true,
    showCreateFolder: true,
    onSelect: handleSizeChartSelect,
    onCancel: () => setShowSizeChartManager(false),
  };

  const variantImageManagerConfig: FileManagerConfig = {
    allowedFileTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ],
    maxFileSize: 10 * 1024 * 1024,
    maxSelections: 5,
    selectionMode: "multiple",
    showUpload: true,
    showCreateFolder: true,
    onSelect: handleVariantImageSelect,
    onCancel: () => {
      setShowVariantImageManager(false);
      setSelectedVariantId("");
    },
  };

  // Generate all possible variant combinations from options (Cartesian product)
  const generateAllCombinations = (options: VariantOption[]): string[][] => {
    if (options.length === 0) return [];
    
    const result: string[][] = [];
    
    const generate = (index: number, current: string[]) => {
      if (index === options.length) {
        result.push([...current]);
        return;
      }
      
      const values = options[index].values;
      if (values.length === 0) {
        // If no values for this option, skip it
        generate(index + 1, current);
        return;
      }
      
      for (const value of values) {
        current[index] = value;
        generate(index + 1, current);
      }
    };
    
    generate(0, new Array(options.length));
    return result;
  };

  // Match a variant from API to a generated combination
  const matchVariantToCombination = (
    combination: string[],
    apiVariant: any
  ): boolean => {
    // Extract option values from API variant (option1, option2, option3)
    const variantOptions = [
      apiVariant.option1,
      apiVariant.option2,
      apiVariant.option3,
    ].filter(Boolean);
    
    // Match if all option values match the combination
    if (variantOptions.length !== combination.length) return false;
    
    return combination.every((value, index) => value === variantOptions[index]);
  };

  const AddMetafield = (key: string, type: string, value: string) => {
    const newMetafield = { key, type, value };
    const existingMetafield = metafields.find((m: { key: string }) => m.key === key);
    if (existingMetafield) {
      setMetafields((prev) => prev.map((m) => (m.key === key ? newMetafield : m)));
    } else {
      setMetafields((prev) => [...prev, newMetafield]);
    }
  };

  const validateProduct = useCallback(
    (data: Product, isPublishing: boolean) => {
      const errors: string[] = [];
      if (!data.title?.trim()) errors.push("Product title is required");

      if (!isPublishing) return errors;

      const descriptionText = (() => {
        if (typeof description === "string") {
          try {
            const parsed = JSON.parse(description);
            if (Array.isArray(parsed?.ops)) {
              return parsed.ops.map((op: { insert?: unknown }) => String(op.insert ?? "")).join("");
            }
          } catch {}
          return description;
        }

        if (Array.isArray(description?.ops)) {
          return description.ops.map((op: { insert?: unknown }) => String(op.insert ?? "")).join("");
        }

        return "";
      })();

      const activeVariants = (hasVariants ? variantCombinations : [defaultVariant]).filter(
        (variant) => !variant.id?.startsWith("disabled-"),
      );

      const hasPrice = activeVariants.some((variant) => Number(variant.price) > 0);
      const hasWeight = activeVariants.some(
        (variant) => Number(variant.shippingDetails?.weight ?? 0) > 0,
      );
      const trackedVariantWithoutStock = activeVariants.find(
        (variant) => variant.trackInventory && Number(variant.inventory ?? 0) <= 0,
      );

      if (images.length === 0) errors.push("At least one product image is required");
      if (!descriptionText.trim()) errors.push("Product description is required");
      if (!hasPrice) errors.push("At least one variant price must be greater than 0");
      if (!hasWeight) errors.push("At least one variant weight must be greater than 0");
      if (trackedVariantWithoutStock) {
        errors.push("Tracked inventory variants must have stock greater than 0");
      }

      return errors;
    },
    [defaultVariant, description, hasVariants, images.length, variantCombinations],
  );

  const { showLoading, hideLoading } = useGlobalLoading();

  const submitProduct = useCallback(
    (data: Product, asDraft: boolean, skipValidation = false) => {
      try {
        const w = window as Window & { __blockedSubmit?: boolean };
        if (w.__blockedSubmit) return;
      } catch {}

      if (!skipValidation) {
        const validationErrors = validateProduct(data, !asDraft);
        if (validationErrors.length > 0) {
          // For publish attempts that fail validation, don't show error toast
          // Instead return the errors to be handled by the caller
          if (!asDraft) {
            return validationErrors;
          }
          // For drafts, show error toast
          toast.error(`Validation failed: ${validationErrors.join(", ")}`);
          return validationErrors;
        }
      }

      const statusMessage = asDraft
        ? isEditMode
          ? "Saving product draft..."
          : "Creating product draft..."
        : isEditMode
          ? "Publishing product..."
          : "Creating product...";
      showLoading(statusMessage);

      const mappedOptions = (variantOptions || []).map((opt, idx) => ({
        key: opt.title ? opt.title.toLowerCase().replace(/\s+/g, "_") : `option_${idx + 1}`,
        title: opt.title || `Option ${idx + 1}`,
        position: idx + 1,
        values: opt.values || [],
      }));

      const sourceVariants = hasVariants ? variantCombinations : [defaultVariant];
      // Filter out disabled variants before sending to server
      const activeSourceVariants = (sourceVariants || []).filter(
        (v: VariantMappingData) => !v.id?.startsWith('disabled-')
      );
      const mappedVariants = activeSourceVariants.map((v: VariantMappingData, i: number) => {
        const sku = v.sku ?? `sku-${i}`;
        // Generate title from options if not explicitly set
        // First try individual option fields, then fall back to options array
        const optionsForTitle = [v.option1, v.option2, v.option3]
          .filter((opt): opt is string => Boolean(opt && opt.trim() !== ""));
        const optionsFromArray = (v.options ?? [])
          .filter((opt): opt is string => Boolean(opt && opt.trim() !== "" && opt !== "Default"));
        const generatedTitle = optionsForTitle.length > 0
          ? optionsForTitle.join(" / ")
          : (optionsFromArray.length > 0 ? optionsFromArray.join(" / ") : null);
        const title = v.title && v.title.trim() !== "" && v.title !== "Default Variant"
          ? v.title
          : (generatedTitle ?? v.optionString ?? data.title ?? "");
        const hsCodeVal = v.hsCode ?? v.hs_code ?? undefined;
        const midVal = v.mid ?? v.mId ?? undefined;
        const descriptionVal = v.description ?? "";
        const inventoryVal = Number(v.inventory ?? 0);
        const priceVal = Number(v.price ?? 0);
        const listingPriceVal = Number(v.listingPrice ?? v.listing_price ?? 0);
        const optionsArr: string[] = v.options ?? [v.option1, v.option2, v.option3].filter((item): item is string => Boolean(item));

        const mfRaw = v.metafields;
        const variantMetafields = Array.isArray(mfRaw)
          ? mfRaw
          : typeof mfRaw === "object" && mfRaw !== null
          ? Object.keys(mfRaw).map((k) => ({ key: k, type: "string", value: String((mfRaw as Record<string, unknown>)[k]) }))
          : [];

        const imagesArr: string[] = v.images ?? (v.image ? [v.image] : []);

        const shippingRaw = v.shipping_details ?? v.shippingDetails ?? {};
        const shipping = {
          weight: Number(shippingRaw.weight ?? 0),
          weight_unit: (shippingRaw as ApiShippingDetails).weight_unit ?? (shippingRaw as ShippingDetails).weightUnit ?? "g",
          width: Number(shippingRaw.width ?? 0),
          height: Number(shippingRaw.height ?? 0),
          length: Number(shippingRaw.length ?? 0),
          shipping_class: (shippingRaw as ApiShippingDetails).shipping_class ?? (shippingRaw as ShippingDetails).shippingClass ?? "",
          free_shipping: !!((shippingRaw as ApiShippingDetails).free_shipping ?? (shippingRaw as ShippingDetails).freeShipping),
          shipping_price: Number((shippingRaw as ApiShippingDetails).shipping_price ?? (shippingRaw as ShippingDetails).shippingPrice ?? 0),
        };

        return {
          ...(isEditMode && (v as any).id ? { id: (v as any).id } : {}),
          ...(isEditMode ? { product_id: id } : {}),
          sku,
          product_slug: data.title || "",
          title,
          hs_code: hsCodeVal,
          mid: midVal,
          description: descriptionVal,
          inventory: inventoryVal,
          track_inventory: !!v.trackInventory,
          continue_selling_oos: !continueSelling,
          taxable: taxable,
          price: priceVal,
          listing_price: listingPriceVal,
          option1: optionsArr[0] ?? "",
          option2: optionsArr[1] ?? "",
          option3: optionsArr[2] ?? "",
          metafields: variantMetafields,
          images: imagesArr,
          shipping_details: shipping,
        };
      });

      const taxIds = selectedTaxIds.length > 0
        ? selectedTaxIds
        : (metafields || [])
            .filter((m: any) => m.key === "taxId")
            .map((m: any) => m.value)
            .filter(Boolean);

      const filteredMetafields = (metafields || [])
        .filter((m) => !["taxId", "taxRate", "taxName"].includes(m.key))
        .map((m) => ({ key: m.key, type: m.type, value: Array.isArray(m.value) ? m.value.join(", ") : String(m.value) }));

      const payload: import("@/types/crud").CreateRequest<Product> = {
        ...(isEditMode ? { id } : {}),
        slug: data.title ? data.title.toLowerCase().replace(/\s+/g, "-") : data.slug || "",
        title: data.title?.trim(),
        tags: tags,
        status: asDraft ? "DRAFT" : "ACTIVE",
        price: mappedVariants.length > 0 ? mappedVariants[0].price : 0,
        compareAtPrice: mappedVariants.length > 0 ? mappedVariants[0].listing_price : 0,
        taxable: taxable,
        shippingWeight: mappedVariants.length > 0 ? mappedVariants[0].shipping_details?.weight || 0 : 0,
        continue_selling_oos: !continueSelling,
        description: typeof description === "object" ? JSON.stringify(description) : description,
        metafields: filteredMetafields,
        taxIds: taxIds,
        options: mappedOptions,
        variants: mappedVariants,
        images: images,
        ...(categoryId.trim() ? { collectionId: categoryId.trim() } : {}),
      };

      if (isEditMode && id) {
        updateItem(id, payload);
      } else {
        createItem(payload);
      }
    },
    [
      validateProduct,
      productVariants,
      hasVariants,
      variantOptions,
      variantCombinations,
      defaultVariant,
      selectedTaxIds,
      continueSelling,
      categoryId,
      images,
      tags,
      taxable,
      metafields,
      description,
      id,
      isEditMode,
      showLoading,
      createItem,
      updateItem,
    ],
  );

  const _onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // noop - buttons handle submit
  };

  const buildFormData = useCallback((): Product => {
    return {
      title,
      slug: title ? title.toLowerCase().replace(/\s+/g, "-") : "",
      tags,
      description: typeof description === "object" ? JSON.stringify(description) : description,
      images,
      taxable,
      continue_selling_oos: continueSelling,
    } as Product;
  }, [title, tags, description, images, taxable, continueSelling]);

  const handleSaveDraft = useCallback(() => {
    if (isEditMode) {
      setShowDraftConfirmDialog(true);
    } else {
      const formData = buildFormData();
      resetFormState();
      setIsDraft(true);
      submitProduct(formData, true);
    }
  }, [submitProduct, resetFormState, isEditMode, buildFormData]);

  const handleConfirmDraft = useCallback(() => {
    const formData = buildFormData();
    resetFormState();
    setIsDraft(true);
    submitProduct(formData, true);
    setShowDraftConfirmDialog(false);
  }, [submitProduct, resetFormState, buildFormData]);

  const handlePublish = useCallback(() => {
    const formData = buildFormData();
    const validationErrors = validateProduct(formData, true);

    if (validationErrors.length > 0) {
      setPublishValidationErrors(validationErrors);
      setShowSaveAsDraftDialog(true);
      return;
    }

    const canPublish = isStoreKycApprovedForPublishing(currentStoreInfo);

    // Draft creation must remain available even when KYC is not approved.
    // Only publishing is gated here.
    if (!canPublish) {
      setPublishValidationErrors([
        "Complete KYC approval in Settings > Business Info before publishing products.",
      ]);
      setShowSaveAsDraftDialog(true);
      return;
    }

    resetFormState();
    setIsDraft(false);
    const errors = submitProduct(formData, false);

    // If there are validation errors, show the save-as-draft dialog
    if (errors && Array.isArray(errors) && errors.length > 0) {
      setPublishValidationErrors(errors);
      setShowSaveAsDraftDialog(true);
    }
  }, [submitProduct, resetFormState, buildFormData, validateProduct, currentStoreInfo]);

  const handleContinueSellingChange = useCallback((checked: boolean) => {
    setContinueSelling(checked);
    setDefaultVariant((prev) => ({ ...prev, trackInventory: checked }));
    setVariantCombinations((prev) =>
      prev.map((variant) => ({ ...variant, trackInventory: checked })),
    );
  }, []);

  const handleSaveAsDraftFromPublish = useCallback(() => {
    const formData = buildFormData();
    resetFormState();
    setIsDraft(true);
    setShowSaveAsDraftDialog(false);
    setPublishValidationErrors([]);
    // Skip validation when saving as draft from failed publish attempt
    submitProduct(formData, true, true);
  }, [submitProduct, resetFormState, buildFormData]);

  const getProductData = useCallback((productId: string) => {
    fetchItem(productId);
  }, [fetchItem]);

  useEffect(() => {
    console.log('[ProductCreate] Product data useEffect', {
      hasCurrentProduct: !!currentProduct,
      currentProductId: currentProduct?.id,
      urlId: id,
      hasVariants: currentProduct?.variants?.length,
      hasOptions: currentProduct?.options?.length,
    });

    if (currentProduct && id) {
      const data = currentProduct;
      console.log('[ProductCreate] Loading product data:', {
        productId: data.id,
        title: data.title,
        variantsCount: data.variants?.length,
        optionsCount: data.options?.length,
        variants: data.variants?.map((v: any) => ({ id: v.id, option1: v.option1, option2: v.option2, sku: v.sku })),
        options: data.options,
      });
      // populate state similar to Next page (omitted detailed form setValue since we don't use react-hook-form here)
      setTitle(isDuplicateMode ? `${data.title || ""} (Copy)` : data.title || "");
      setIsDraft(data.status === "DRAFT");
      setContinueSelling(!!data.continue_selling_oos);
      setTaxable(!!data.taxable);
      setCategoryId(data.collectionId || "");
      setImages(data.images || []);
      
      // Populate assets from API response
      if (data.assets && Array.isArray(data.assets)) {
        setAssets(data.assets);
      } else {
        // If no assets provided, clear the array
        setAssets([]);
      }
      
      setTags(data.tags || []);
      const convertedMetafields: MetafieldValue[] = (data.metafields || []).map((m: any) => ({ key: m.key, type: m.type, value: m.value }));
      setMetafields(convertedMetafields);

      const productTaxIds = (data as any).tax_ids;
      if (productTaxIds && productTaxIds.length > 0) setSelectedTaxIds(productTaxIds);

      if (data.description) {
        if (typeof data.description === "object") setDescription(data.description);
        else if (typeof data.description === "string") {
          try {
            setDescription(JSON.parse(data.description));
          } catch {
            setDescription({ ops: [{ insert: data.description }] });
          }
        }
      } else setDescription({ ops: [] });

      // Load options from API
      if (Array.isArray(data.options) && data.options.length > 0) {
        const mapped = data.options.map((opt: any) => ({
          id: opt.key || opt.title || "",
          title: opt.title || opt.key || "",
          values: Array.isArray(opt.values) ? opt.values : []
        }));
        // NOTE: setVariantOptions is called AFTER setVariantCombinations below
        // to ensure variants are available when VariantTable's useEffect runs

        // Generate all possible combinations from options
        const allCombinations = generateAllCombinations(mapped as VariantOption[]);
        
        // Create a map of existing variants by their option values
        const existingVariantsMap = new Map<string, any>();
        if (data.variants && Array.isArray(data.variants)) {
          data.variants.forEach((variant: any) => {
            const optionKey = [
              variant.option1,
              variant.option2,
              variant.option3,
            ].filter(Boolean).join("::");
            existingVariantsMap.set(optionKey, variant);
          });
        }
        
        // Process all possible combinations and match with existing variants
        const matchedCombinations: VariantCombination[] = [];
        const deletedVariantsList: VariantCombination[] = [];
        
        allCombinations.forEach((combination: string[], index: number) => {
          const optionKey = combination.join("::");
          const existingVariant = existingVariantsMap.get(optionKey);
          
          if (existingVariant) {
            // Found matching variant - populate with full data
            
            // Build variantAssets array from API data (check both 'assets' and 'variant_assets' fields)
            const rawAssets = existingVariant.assets || existingVariant.variant_assets || [];
            const variantAssets = rawAssets.map((va: any) => ({
              asset_id: va.asset_id || va.asset?.id,
              asset: va.asset ? {
                id: va.asset.id,
                path: va.asset.path,
                original_name: va.asset.original_name,
                size: va.asset.size,
                content_type: va.asset.content_type,
              } : undefined,
            }));
            
            const combo: VariantCombination = {
              id: isDuplicateMode
                ? `new-variant-${Date.now()}-${index}`
                : existingVariant.id || `variant-${Math.random().toString(36).slice(2, 9)}`,
              options: [...combination],
              optionString: combination.join(" / "),
              image: existingVariant.images && existingVariant.images.length > 0 ? existingVariant.images[0] : undefined,
              images: existingVariant.images && Array.isArray(existingVariant.images) ? existingVariant.images : [],
              variantAssets: variantAssets.length > 0 ? variantAssets : undefined,
              price: typeof existingVariant.price === "number" ? existingVariant.price : Number(existingVariant.price) || 0,
              listingPrice: typeof existingVariant.listing_price === "number" ? existingVariant.listing_price : Number(existingVariant.listing_price) || 0,
              sku: isDuplicateMode ? "" : existingVariant.sku || "",
              hsCode: existingVariant.hs_code ?? existingVariant.hsCode ?? undefined,
              mid: existingVariant.mid ?? existingVariant.mId ?? undefined,
              barcode: existingVariant.barcode || "",
              trackInventory: !!existingVariant.track_inventory,
              inventory: existingVariant.inventory || 0,
              title: existingVariant.title || "",
              description: existingVariant.description || "",
              shippingDetails: {
                weight: existingVariant.shipping_details?.weight || 0,
                weightUnit: existingVariant.shipping_details?.weight_unit || existingVariant.shipping_details?.weightUnit || "g",
                width: existingVariant.shipping_details?.width || 0,
                height: existingVariant.shipping_details?.height || 0,
                length: existingVariant.shipping_details?.length || 0,
                shippingClass: existingVariant.shipping_details?.shipping_class || existingVariant.shipping_details?.shippingClass || "",
                freeShipping: !!existingVariant.shipping_details?.free_shipping,
                shippingPrice: existingVariant.shipping_details?.shipping_price || existingVariant.shipping_details?.shippingPrice || 0,
              },
              metafields: existingVariant.metafields || {},
            };
            matchedCombinations.push(combo);
            // Remove from map to track which variants we've processed
            existingVariantsMap.delete(optionKey);
          } else {
            // No matching variant - create disabled placeholder entry
            const combo: VariantCombination = {
              id: `disabled-${Date.now()}-${index}`,
              options: [...combination],
              optionString: combination.join(" / "),
              image: undefined,
              images: [],
              price: 0,
              listingPrice: 0,
              sku: "",
              hsCode: undefined,
              mid: undefined,
              barcode: "",
              trackInventory: false,
              inventory: 0,
              title: "",
              description: "",
              shippingDetails: {
                weight: 0,
                weightUnit: "g",
                width: 0,
                height: 0,
                length: 0,
                shippingClass: "",
                freeShipping: false,
                shippingPrice: 0,
              },
              metafields: {},
            };
            matchedCombinations.push(combo);
          }
        });
        
        // Any remaining variants in the map don't match current option structure (deleted)
        existingVariantsMap.forEach((variant: any, optionKey: string) => {
          const optionValues = optionKey.split("::");
          
          // Build variantAssets array for deleted variants too (check both 'assets' and 'variant_assets' fields)
          const rawDeletedAssets = variant.assets || variant.variant_assets || [];
          const variantAssets = rawDeletedAssets.map((va: any) => ({
            asset_id: va.asset_id || va.asset?.id,
            asset: va.asset ? {
              id: va.asset.id,
              path: va.asset.path,
              original_name: va.asset.original_name,
              size: va.asset.size,
              content_type: va.asset.content_type,
            } : undefined,
          }));
          
          const deletedCombo: VariantCombination = {
            id: variant.id || `deleted-${Math.random().toString(36).slice(2, 9)}`,
            options: optionValues,
            optionString: optionValues.join(" / "),
            image: variant.images && variant.images.length > 0 ? variant.images[0] : undefined,
            images: variant.images && Array.isArray(variant.images) ? variant.images : [],
            variantAssets: variantAssets.length > 0 ? variantAssets : undefined,
            price: typeof variant.price === "number" ? variant.price : Number(variant.price) || 0,
            listingPrice: typeof variant.listing_price === "number" ? variant.listing_price : Number(variant.listing_price) || 0,
            sku: variant.sku || "",
            hsCode: variant.hs_code ?? variant.hsCode ?? undefined,
            mid: variant.mid ?? variant.mId ?? undefined,
            barcode: variant.barcode || "",
            trackInventory: !!variant.track_inventory,
            inventory: variant.inventory || 0,
            title: variant.title || "",
            description: variant.description || "",
            shippingDetails: {
              weight: variant.shipping_details?.weight || 0,
              weightUnit: variant.shipping_details?.weight_unit || variant.shipping_details?.weightUnit || "g",
              width: variant.shipping_details?.width || 0,
              height: variant.shipping_details?.height || 0,
              length: variant.shipping_details?.length || 0,
              shippingClass: variant.shipping_details?.shipping_class || variant.shipping_details?.shippingClass || "",
              freeShipping: !!variant.shipping_details?.free_shipping,
              shippingPrice: variant.shipping_details?.shipping_price || variant.shipping_details?.shippingPrice || 0,
            },
            metafields: variant.metafields || {},
          };
          deletedVariantsList.push(deletedCombo);
        });
        
        // Set variants FIRST, then options - this ensures variants are available
        // when VariantTable's useEffect detects the options change
        console.log('[ProductCreate] Setting variants from API:', {
            matchedCombinationsCount: matchedCombinations.length,
            matchedCombinations: matchedCombinations.map(v => ({ id: v.id, optionString: v.optionString, sku: v.sku, options: v.options })),
            mappedOptions: mapped,
        });
        setVariantCombinations(matchedCombinations);
        setDeletedVariants(deletedVariantsList);
        setVariantOptions(mapped as VariantOption[]);
        setHasVariants(true);

        // Log after state updates are queued
        console.log('[ProductCreate] State updates queued - variants then options');
      } else {
        setVariantOptions([]);
        setDeletedVariants([]);
        
        // Handle default variant (no options)
        if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
          const first: VariantMappingData = data.variants[0];
          const hasMultipleVariants = data.variants.length > 1 || 
            (first.option1 && first.option1 !== "Default");
          
          if (!hasMultipleVariants) {
            setVariantCombinations([]);
            setHasVariants(false);
            setDefaultVariant((prev) => ({
              ...prev,
              id: isEditMode ? first.id || prev.id : prev.id,
              options: ["Default"],
              optionString: "Default Variant",
              price: Number(first.price) || prev.price,
              listingPrice: Number(first.listing_price) || prev.listingPrice,
              sku: isDuplicateMode ? "" : first.sku || prev.sku,
              inventory: Number(first.inventory) || prev.inventory,
              trackInventory: !!first.track_inventory,
              title: first.title || prev.title,
              description: first.description || prev.description,
              shippingDetails: {
                weight: first.shipping_details?.weight || prev.shippingDetails.weight,
                weightUnit: first.shipping_details?.weight_unit || prev.shippingDetails.weightUnit,
                width: first.shipping_details?.width || prev.shippingDetails.width,
                height: first.shipping_details?.height || prev.shippingDetails.height,
                length: first.shipping_details?.length || prev.shippingDetails.length,
                shippingClass: first.shipping_details?.shipping_class || prev.shippingDetails.shippingClass,
                freeShipping: !!first.shipping_details?.free_shipping,
                shippingPrice: first.shipping_details?.shipping_price || prev.shippingDetails.shippingPrice,
              },
              metafields: Array.isArray(first.metafields) ? first.metafields : first.metafields && typeof first.metafields === "object" ? Object.entries(first.metafields).map(([key, value]) => ({ key, type: "string", value: String(value) })) : prev.metafields,
            }));
          }
        }
      }
    }
  }, [currentProduct, id, isDuplicateMode, isEditMode]);

  useEffect(() => {
    if (success && !loading) {
      hideLoading();
      toast.dismiss();
      toast.success(isEditMode ? "Product updated successfully" : "Product created successfully");
      resetFormState();
      if (isDuplicateMode && !isEditMode) {
        navigate(`/${storeType}/${storeId}/product`);
        return;
      }
      if (!isEditMode && isDraft && currentProduct?.id) {
        navigate(`/${storeType}/${storeId}/product/create?q=${currentProduct.id}`, { replace: true });
        return;
      }
      if (!isEditMode && !isDraft) {
        setTitle("");
        setContinueSelling(false);
        setTaxable(false);
        setCategoryId("");
        setSelectedTaxIds([]);
        setImages([]);
        setVideos([]);
        setSizeCharts([]);
        setTags([]);
        setMetafields([]);
        setDescription({ ops: [] });
      }
    }
  }, [
    success,
    loading,
    hideLoading,
    isEditMode,
    isDuplicateMode,
    resetFormState,
    isDraft,
    currentProduct?.id,
    navigate,
    storeType,
    storeId,
  ]);

  useEffect(() => {
    if (error) {
      hideLoading();
      toast.dismiss();
      toast.error(error.message);
    }
  }, [error, hideLoading]);

  useEffect(() => {
    if (sourceProductId) getProductData(sourceProductId);
  }, [sourceProductId, getProductData]);

  useEffect(() => {
    // Always fetch fresh categories and taxes when component mounts
    // Add timestamp to prevent caching
    dispatch(fetchCategoriesRequest({
      page: 1,
      pageSize: 100,
      _t: Date.now() // Cache busting parameter
    } as any));
    dispatch(fetchTaxesRequest({
      page: 1,
      page_size: 100,
      _t: Date.now() // Cache busting parameter
    } as any));
  }, [dispatch]);

  useEffect(() => {
    const submitListener = (ev: Event) => {
      try {
        ev.preventDefault();
        (ev as any).stopImmediatePropagation?.();
      } catch (err) {}
    };
    window.addEventListener("submit", submitListener, { capture: true });
    return () => window.removeEventListener("submit", submitListener, { capture: true });
  }, []);

  useEffect(() => setCategory(catergoryList.items || []), [catergoryList.items]);

  useEffect(() => {
    setTaxes(taxList.items || []);
    const currentProductTaxIds = currentProduct ? (currentProduct as Product & { tax_ids?: string[] }).tax_ids : null;
    if (currentProduct && currentProductTaxIds && currentProductTaxIds.length > 0 && selectedTaxIds.length === 0) {
      setSelectedTaxIds(currentProductTaxIds);
    }
  }, [taxList.items, currentProduct, selectedTaxIds]);

  const navigationItems = [
    { label: "Products", href: `/${storeType}/${storeId}/product` },
    { label: isDuplicateMode ? "Duplicate Product" : id ? "Edit Product" : "Add Product", isActive: true },
  ];

  const handleTableKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (e.key === "Enter" && target instanceof HTMLInputElement) {
      if (!target.closest("table")) return;
      e.preventDefault();
      const currentTabIndex = parseInt(target.getAttribute("tabindex") || "0");
      const nextTabIndex = currentTabIndex + 1;
      const nextInput = document.querySelector(`[tabindex="${nextTabIndex}"]`) as HTMLElement;
      if (nextInput) nextInput.focus();
    }
  };

  // Show loading indicator when editing and data is being fetched or hasn't loaded yet
  // Also show when navigating to a different product (currentProduct.id doesn't match the URL id)
  const isLoadingProduct = sourceProductId && (itemLoading || !currentProduct || currentProduct.id !== sourceProductId);
  const primaryActionLabel = isEditMode && !isDraft ? "Update Product" : "Publish Product";

  if (isLoadingProduct) {
    return (
      <div className="p-2 mb-6 relative">
        <NavigationHeader items={navigationItems} />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 mb-6 relative">
      <form onSubmit={_onSubmit}>
        <NavigationHeader items={navigationItems} />
        <div className="flex items-center justify-between w-full mb-4">
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <ButtonPrimary type="button" onClick={handleSaveDraft} className="rounded-full" disabled={loading}>
              <CloudDownload size={16} className="mr-1" /> Save Draft
            </ButtonPrimary>
            <ButtonPrimary type="button" onClick={handlePublish} className="rounded-full" disabled={loading}>
              <Save size={16} className="mr-1" /> {primaryActionLabel}
            </ButtonPrimary>
          </div>
        </div>

        <div className="flex flex-col gap-[10px]">
          <FormFieldWrapper title="Product Information">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <StandardFormInput label="Product Name" required type="text" placeholder="Product Name" tabIndex={1} value={title} onChange={(e) => setTitle(e.target.value)} />
              <StandardFormSelect label="Collection" value={categoryId} onValueChange={setCategoryId} options={(category?.filter((item) => item.id).map((item) => ({ label: item.title, value: item.id! })) as any) || []} placeholder="Select Collection">
                <div className="w-full p-2 border-b border-border">
                  <Link className="text-sm hover:text-primary transition-colors" to={`/${storeType}/${storeId}/product/collection/create`}>
                    + Create New Collection
                  </Link>
                </div>
              </StandardFormSelect>
              <StandardFormTagInput label="Tags" tags={tags} onTagsChange={setTags} placeholder="Enter tags, press comma to add" />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[13px] font-normal text-muted-foreground">Taxable Product</span>
              <Switch checked={taxable} onCheckedChange={(checked) => setTaxable(checked)} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[13px] font-normal text-muted-foreground">Continue Selling When Out of Stock</span>
              <Switch checked={continueSelling} onCheckedChange={handleContinueSellingChange} />
            </div>

            {taxable && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <StandardFormMultiSelect label="Tax Rates" values={selectedTaxIds} onValuesChange={(values) => {
                  setSelectedTaxIds(values);
                }} options={(taxes?.filter((tax) => tax.id && tax.enabled === true).map((tax) => ({ label: `${tax.name} (${tax.tax_amount}${tax.tax_mode === "inclusive" ? "% inclusive" : "% exclusive"})`, value: tax.id! })) as any) || []} placeholder="Select Tax Rates" />
                <div></div>
                <div></div>
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-[13px] font-normal text-muted-foreground">Product Description</label>
              <QuillEditor key={`description-${id || "new"}`} initialValue={description} onChange={handleDescriptionChange} />
            </div>

            <div className="flex flex-col lg:flex-row gap-[10px]">
              <div className="space-y-1">
                <label className="block text-[13px] font-normal text-muted-foreground">Product Images</label>
                <ImageUpload 
                  images={images} 
                  assets={assets}
                  onChange={HandleImageChange} 
                  type="multiple" 
                  onOpenFileManager={() => setShowImageManager(true)} 
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[13px] font-normal text-muted-foreground">Product Videos</label>
                {/* VideoUpload exists under the original app path; importing in header would be needed if used */}
              </div>
              <div className="space-y-1">
                <label className="block text-[13px] font-normal text-muted-foreground">Size Charts</label>
                {/* SizeChartUpload exists under original app path; omitted to avoid extra imports */}
              </div>
            </div>
          </FormFieldWrapper>

          <FormFieldWrapper title="Product Variants">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-[13px] font-normal text-muted-foreground">Product Contains Variants</span>
                <p className="text-xs text-muted-foreground mt-1">Enable this to create multiple variants with different options (size, color, etc.)</p>
              </div>
              <Switch checked={hasVariants} onCheckedChange={handleVariantToggle} />
            </div>

            {hasVariants ? (
              <>
                <VariantOptions options={variantOptions} onChange={setVariantOptions} maxOptions={3} />
                {(variantOptions.length > 0 || variantCombinations.length > 0) && (
                  <div className="mt-6">
                    <VariantTable 
                      variantOptions={variantOptions} 
                      variants={variantCombinations} 
                      deletedVariants={deletedVariants}
                      onChange={setVariantCombinations} 
                      onVariantDetail={setSelectedVariant} 
                      onImageUpload={handleVariantImageUpload} 
                      onReenableVariant={handleReenableVariant}
                      onEnableVariant={handleEnableVariant}
                      onKeyDown={handleTableKeyDown} 
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="mt-6">
                <VariantTable variantOptions={[]} variants={[defaultVariant]} onChange={(updatedVariants) => {
                  if (updatedVariants.length > 0) setDefaultVariant(updatedVariants[0]);
                }} onVariantDetail={setSelectedVariant} onImageUpload={handleVariantImageUpload} onKeyDown={handleTableKeyDown} />
              </div>
            )}
          </FormFieldWrapper>


          <FormFieldWrapper title="Product Metafields">
            <ProductMetafieldsCard values={metafields} onChange={setMetafields} />
          </FormFieldWrapper>

        </div>
      </form>

      <FileManagerModal isOpen={showImageManager} onClose={() => setShowImageManager(false)} config={imageManagerConfig} />
      <FileManagerModal isOpen={showVideoManager} onClose={() => setShowVideoManager(false)} config={videoManagerConfig} />
      <FileManagerModal isOpen={showSizeChartManager} onClose={() => setShowSizeChartManager(false)} config={sizeChartManagerConfig} />
      <FileManagerModal isOpen={showVariantImageManager} onClose={() => { setShowVariantImageManager(false); setSelectedVariantId(""); }} config={variantImageManagerConfig} />

      <VariantDetailModal variant={selectedVariant} isOpen={selectedVariant !== null} onClose={() => setSelectedVariant(null)} onSave={handleVariantSave} onApplyToAll={handleApplyToAll} />

      <Dialog open={showDraftConfirmDialog} onOpenChange={setShowDraftConfirmDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Save as Draft
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Saving this product as a draft will remove it from your storefront and deactivate it for customers.</p>
              <p className="text-sm font-medium">Are you sure you want to continue?</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-3">
            <ButtonOutline onClick={() => setShowDraftConfirmDialog(false)} className="min-w-[100px] h-10">Cancel</ButtonOutline>
            <ButtonPrimary onClick={handleConfirmDraft} className="bg-amber-500 hover:bg-amber-600 text-white min-w-[140px] h-10">
              <CloudDownload size={16} className="mr-1" /> Save as Draft
            </ButtonPrimary>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Draft from Failed Publish Dialog */}
      <Dialog open={showSaveAsDraftDialog} onOpenChange={setShowSaveAsDraftDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Product Cannot Be Published
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                This product cannot be published yet because it is missing required information or your KYC is not approved:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {publishValidationErrors.map((error, index) => (
                  <li key={index} className="text-red-600">{error}</li>
                ))}
              </ul>
              <p className="text-sm font-medium pt-2">
                Would you like to save this product as a draft instead? You can complete the required information and publish it later.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-3">
            <ButtonOutline
              onClick={() => {
                setShowSaveAsDraftDialog(false);
                setPublishValidationErrors([]);
              }}
              className="min-w-[100px] h-10"
            >
              Cancel
            </ButtonOutline>
            <ButtonPrimary
              onClick={handleSaveAsDraftFromPublish}
              className="bg-amber-500 hover:bg-amber-600 text-white min-w-[140px] h-10"
            >
              <CloudDownload size={16} className="mr-1" />
              Save as Draft
            </ButtonPrimary>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewProduct;
